/**
 * Saying the right thing, chosen by what is true.
 *
 * This is `trashTalk.js` with the trash talk taken out of it. That module has
 * worked since step 52 and the rule it is built on turned out to be general:
 * **a list of lines, ordered, and the first one whose conditions all hold is
 * the one that gets said.** No dice anywhere, so the same state says the same
 * thing every time — which is the entire mechanic, because the point is being
 * *noticed*. A random pick from a matching set would look like it was working
 * while throwing that away.
 *
 * It was extracted when Sala arrived (step 62). He is a second voice keyed on a
 * different set of facts, on different screens, and the choice was between a
 * second copy of this arithmetic and one shared copy with two tables in front
 * of it. This project has paid twice for a second copy of a lookup (gotcha 29),
 * and a selection rule is worse than a lookup: two of them drift *silently*,
 * because both keep answering.
 *
 * What a script brings is three things — its own `FACTS` table, its own set of
 * voices, and its own JSON. What it gets back is the matching, the ordering
 * rule and a validator that refuses the four ways a script of lines goes wrong.
 *
 * Pure, and it holds no DOM: `src/ui` renders what this returns.
 */

export class LinesConfigError extends Error {}

/**
 * The four ways a fact can be matched, and what a condition may look like.
 *
 * `number` takes `{ min, max, is }` or a bare number, `flag` takes a boolean,
 * `text` takes a string or `{ is }`, and `list` takes `{ has }` or `{ lacks }`.
 */
export const KINDS = Object.freeze(['number', 'flag', 'text', 'list']);

/** The matcher shapes each kind will read. Anything else is silently ignored. */
const SHAPES = Object.freeze({
  number: ['min', 'max', 'is'],
  text: ['is'],
  list: ['has', 'lacks'],
});

/** Whether one condition holds against one fact. */
export function holds(kind, fact, want) {
  switch (kind) {
    case 'number': {
      if (typeof want === 'number') return fact === want;
      if (want.is !== undefined && fact !== want.is) return false;
      if (want.min !== undefined && !(fact >= want.min)) return false;
      if (want.max !== undefined && !(fact <= want.max)) return false;
      return true;
    }
    case 'flag':
      return Boolean(fact) === Boolean(want);
    case 'text':
      return fact === (typeof want === 'string' ? want : want.is);
    case 'list': {
      const list = fact ?? [];
      if (want.has !== undefined && !list.includes(want.has)) return false;
      if (want.lacks !== undefined && list.includes(want.lacks)) return false;
      return true;
    }
    default:
      return false;
  }
}

/** Whether every condition on a line holds. A line with none always does. */
export function matches(line, facts, table) {
  for (const [key, want] of Object.entries(line.when ?? {})) {
    if (!holds(table[key], facts[key], want)) return false;
  }
  return true;
}

/**
 * The line this voice says, given the state.
 *
 * Never null for a validated script: every voice is guaranteed an
 * unconditional line and the validator is what enforces it.
 */
export function pickLine(lines, facts, { voice = null, field = 'speaker', table } = {}) {
  for (const line of lines) {
    if (voice !== null && line[field] !== voice) continue;
    if (matches(line, facts, table)) return line;
  }
  return null;
}

/**
 * Throws on a script that cannot mean what it says.
 *
 * Four refusals, and only the first is about types:
 *
 *   - a fact that does not exist, which can only be a typo — and a typo in a
 *     condition is a line that silently never fires;
 *   - a matcher shape the reader would ignore, which is the same failure
 *     wearing a different hat;
 *   - a duplicate id;
 *   - **a line below a catch-all**, which could never be said. A line with no
 *     conditions matches everything after it for that voice, so it has to be
 *     the last thing they can say.
 *
 * And it insists every voice *has* a catch-all, because a screen with a blank
 * where a line should be is the thing this whole arrangement exists to prevent.
 */
export function validateLines(
  config,
  { facts, voices, field = 'speaker', what = 'a script', Err = LinesConfigError } = {}
) {
  const lines = config?.lines;
  if (!Array.isArray(lines) || lines.length === 0) {
    throw new Err(`${what} has no lines`);
  }

  const seen = new Set();
  /** Whether a voice has already had its catch-all. */
  const closed = new Set();

  for (const line of lines) {
    if (!line?.id) throw new Err(`a line has an id of ${JSON.stringify(line?.id)}`);
    if (seen.has(line.id)) throw new Err(`"${line.id}" is in the file twice`);
    seen.add(line.id);

    const voice = line[field];
    if (!voices.includes(voice)) {
      throw new Err(`"${line.id}" is ${field} "${voice}"; there is ${voices.join(', ')}`);
    }
    if (typeof line.say !== 'string' || !line.say.trim()) {
      throw new Err(`"${line.id}" says nothing`);
    }

    const when = line.when ?? {};
    if (typeof when !== 'object' || Array.isArray(when)) {
      throw new Err(`"${line.id}" has a bad when`);
    }

    if (closed.has(voice)) {
      throw new Err(`"${line.id}" comes after ${voice}'s catch-all and can never be said`);
    }
    if (Object.keys(when).length === 0) closed.add(voice);

    for (const [key, want] of Object.entries(when)) {
      const kind = facts[key];
      if (!kind) {
        throw new Err(
          `"${line.id}" keys on "${key}", which is not a fact; there is ${Object.keys(facts).join(', ')}`
        );
      }
      if (kind === 'flag') {
        if (typeof want !== 'boolean') {
          throw new Err(`"${line.id}" matches ${key} with ${JSON.stringify(want)}`);
        }
        continue;
      }
      // A bare number and a bare string are shorthand for equality and are fine.
      if (kind === 'number' && typeof want === 'number') continue;
      if (kind === 'text' && typeof want === 'string') continue;

      const allowed = SHAPES[kind];
      const keys = Object.keys(want ?? {});
      if (!keys.length || keys.some((one) => !allowed.includes(one))) {
        throw new Err(`"${line.id}" matches ${key} with ${JSON.stringify(want)}`);
      }
    }
  }

  for (const voice of voices) {
    if (!closed.has(voice)) {
      throw new Err(`${voice} has no unconditional line to fall back on`);
    }
  }

  return config;
}

/**
 * A state that satisfies one line's own conditions, laid over a neutral base.
 *
 * Used by the tripwire both scripts have a test for: a line placed below a more
 * general one that its own conditions *also* satisfy is a line nobody will ever
 * hear. The validator cannot see it — both orderings are legal — and only
 * asking can. Shared because the derivation is fiddly and two copies of it
 * would disagree about, for instance, what `{ max: -2 }` implies.
 */
export function deriveState(when = {}, base = {}) {
  const state = { ...base };
  for (const [key, want] of Object.entries(when)) {
    if (typeof want === 'number' || typeof want === 'boolean' || typeof want === 'string') {
      state[key] = want;
    } else if (want.is !== undefined) state[key] = want.is;
    else if (want.min !== undefined) state[key] = want.min;
    else if (want.max !== undefined) state[key] = want.max;
    else if (want.has !== undefined) state[key] = [want.has];
    else if (want.lacks !== undefined) state[key] = [];
  }
  return state;
}
